import { Col, Divider, Modal, Row, Space } from "antd";
import React, { useMemo } from "react";
import Text from "../Text/Text";
import Button from "../Button/Button";
import { StepsStyled } from "../StepAntd/StepAntd";
import StatusBadge from "../Badge/StatusBadge";
import type {
  creditApproversEntity,
  orderCreditEntity,
  soApproversEntity,
} from "../../entities/OrderCreditEntity";
import moment from "moment";
import { normalizeStatus, STATUS_CREDIT } from "../../definitions/creditStatus";

interface Approver {
  num: number;
  userName: string;
  status: string;
  dateApprove?: string;
  displayLabel?: string;
  wasApprovedBeforeReject?: boolean;
}

interface StepItem {
  num: number;
  role: string;
  count: number;
  user: Approver[];
  reason?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  companyCode: string;
  data: {
    creditApprovers: creditApproversEntity | null;
    order: orderCreditEntity | null;
    soApprovers: soApproversEntity | null;
  };
}

const ModalCheckStatusCredit: React.FC<Props> = ({ open, onClose, data, companyCode }) => {
  const creditTabs = localStorage.getItem("creditTabs");

  // ตรวจสอบว่า credit หรือ so มี reject หรือไม่
  const hasCreditReject = useMemo(() => {
    const approvers = data.creditApprovers?.allSequence ?? [];
    return approvers.some((seq) => seq.detail?.some((d) => d.action === "REJECT"));
  }, [data.creditApprovers]);

  const hasSOReject = useMemo(() => {
    const approvers = data.soApprovers?.allSequence ?? [];
    return approvers.some((seq) => seq.detail?.some((d) => d.action === "REJECT"));
  }, [data.soApprovers]);

  // ถ้า credit หรือ so มีคน reject แล้ว ให้แสดง "ไม่อนุมัติ (อัตโนมัติทุกคน)"
  const hasAnyReject = hasCreditReject || hasSOReject;

  const generateSteps = (approversData: any, forceAutoReject: boolean) => {
    const approvers = approversData?.allSequence ?? [];
    const hasReject = approvers.some((seq) => seq.detail?.some((d) => d.action === "REJECT"));

    let rejectInfo: { role?: string; reason?: string; name?: string } | null = null;

    const steps = approvers.map((seq) => {
      const users =
        seq.detail?.map((d, i) => {
          // ถ้า forceAutoReject = true (มี reject จาก credit หรือ so) และคนนี้ไม่ได้เป็นคน reject
          const isAutoReject = (hasReject || forceAutoReject) && d.action !== "REJECT";
          const wasApprovedBeforeReject = isAutoReject && d.action === "APPROVE";
          if (d.action === "REJECT") {
            rejectInfo = {
              role: seq.role,
              reason: d.reason || "-",
              name: d.userStaffName || "-",
            };
          }

          return {
            num: i + 1,
            userName: d.userStaffName || "-",
            dateApprove: d.updateAt ? moment(d.updateAt).format("DD/MM/YYYY, HH:mm") : "-",
            status: isAutoReject ? "REJECT" : d.action,
            displayLabel: isAutoReject ? "ไม่อนุมัติ (อัตโนมัติ)" : "",
            wasApprovedBeforeReject,
          };
        }) || [];

      return {
        num: seq.sequence,
        role: seq.role,
        count: users.length,
        user: users,
      };
    });

    return { steps, rejectInfo };
  };

  const { steps: creditSteps, rejectInfo: creditReject } = useMemo(
    () => generateSteps(data.creditApprovers, hasAnyReject),
    [data.creditApprovers, hasAnyReject],
  );

  const { steps: soSteps, rejectInfo: soReject } = useMemo(
    () => generateSteps(data.soApprovers, hasAnyReject),
    [data.soApprovers, hasAnyReject],
  );
  const getCurrentStep = (steps: StepItem[]) => {
    if (!steps || steps.length === 0) return 0;
    const allUsers = steps.flatMap((step) => step.user);
    const hasPending = allUsers.some((u) => u.status === "PENDING");
    const current = hasPending ? 0 : steps.length;
    return current;
  };

  const currentCredit = getCurrentStep(creditSteps);
  const currentSO = getCurrentStep(soSteps);
  const hasCreditData = creditSteps?.length > 0;
  const hasSOData = soSteps?.length > 0;
  const noData = !hasCreditData && !hasSOData;
  return (
    <Modal
      width={700}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      closable
      centered
      title={
        <Space
          direction='vertical'
          size={2}
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Text fontWeight={600} fontSize={20} color='Text2'>
            เช็คสถานะคำขอวงเงิน
          </Text>
          <Space direction='horizontal' size={2} style={{ display: "flex", alignItems: "center" }}>
            <Text fontFamily='Sarabun'>
              {creditTabs === "ORDER" ? "รหัสคำสั่งซื้อ" : "รหัสคำขอ"} : {data?.order?.orderNo}
            </Text>
            {data?.order?.orderRefNo && (
              <Text fontFamily='Sarabun' color='error'>
                {`(Reorder:${data?.order?.orderRefNo})`}
              </Text>
            )}
          </Space>

          <Text fontFamily='Sarabun'>ชื่อร้านค้า : {data?.order?.customerName}</Text>
        </Space>
      }
      styles={{
        body: {
          maxHeight: 500,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        },
      }}
      footer={
        <div style={{ textAlign: "center" }}>
          <Button title='ปิด' onClick={onClose} />
        </div>
      }
    >
      <Divider />

      {noData && (
        <Text
          color='background4'
          fontFamily='Sarabun'
          style={{ textAlign: "center", display: "block", margin: 12 }}
        >
          ไม่พบข้อมูล
        </Text>
      )}

      {!noData && (
        <>
          {hasCreditData && (
            <>
              <Text fontWeight={600} fontSize={16}>
                การอนุมัติวงเงินเกิน
              </Text>
              <StepSection
                steps={creditSteps}
                current={currentCredit}
                rejectInfo={creditReject}
                emptyText='ไม่มีข้อมูลผู้อนุมัติวงเงินเกิน'
              />
              <Divider />
            </>
          )}

          {hasSOData && (
            <>
              <Text fontWeight={600} fontSize={16}>
                ใบสั่งขาย
              </Text>
              <StepSection
                steps={soSteps}
                current={currentSO}
                rejectInfo={soReject}
                emptyText='ไม่มีข้อมูลผู้อนุมัติใบสั่งขาย'
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

const StepSection: React.FC<{
  steps: StepItem[];
  current: number;
  emptyText: string;
  rejectInfo?: { role?: string; reason?: string; name?: string } | null;
}> = ({ steps, current, emptyText, rejectInfo }) => {
  if (!steps.length)
    return (
      <Text
        color='background4'
        fontFamily='Sarabun'
        style={{ textAlign: "center", display: "block" }}
      >
        {emptyText}
      </Text>
    );
  return (
    <div style={{ marginTop: 14 }}>
      <StepsStyled
        current={current}
        direction='vertical'
        items={steps.map((step) => ({
          title: (
            <Text fontWeight={700}>
              ผู้อนุมัติลำดับที่ {step.num} &nbsp;:&nbsp;
              <Text>
                {step.role}&nbsp;จำนวน&nbsp;{step.count} คน
              </Text>
            </Text>
          ),
          description: (
            <div style={{ paddingLeft: 20 }}>
              <Space direction='vertical' size={4} style={{ width: "100%" }}>
                {step.user.map((u) => (
                  <ApproverItem key={u.num} step={step} approver={u} />
                ))}
              </Space>
            </div>
          ),
        }))}
      />

      {rejectInfo?.reason && (
        <div style={{ paddingLeft: "9%" }}>
          <Text fontWeight={700}>เหตุผล</Text>
          <br />
          <Text>{rejectInfo.reason}</Text>
        </div>
      )}
    </div>
  );
};

const ApproverItem: React.FC<{ step: StepItem; approver: Approver }> = ({ step, approver }) => {
  const normalizedStatus = normalizeStatus(approver.status);
  const meta = STATUS_CREDIT[normalizedStatus] || {};
  const approvedMeta = STATUS_CREDIT[normalizeStatus("APPROVE")] || {};

  return (
    <Row justify='space-between' align='middle'>
      <Col>
        <Space direction='vertical' size={1}>
          {approver.status !== "PENDING" ? (
            <Text fontWeight={700}>
              คนที่&nbsp;{approver.num}&nbsp;<Text>{approver.userName}</Text>
              <br />
              <Text>{step.role || "-"}</Text>
            </Text>
          ) : (
            <Text fontWeight={700}>
              คนที่ &nbsp;{approver.num} &nbsp;
              <Text>{step.role || "-"}</Text>
            </Text>
          )}
        </Space>
      </Col>
      <Col>
        <Space direction='vertical' size={1} style={{ display: "flex", alignItems: "end" }}>
          {approver.wasApprovedBeforeReject ? (
            <Space direction='horizontal' size={4}>
              <StatusBadge
                label={approvedMeta.label || "อนุมัติแล้ว"}
                bgColor={approvedMeta.color}
              />
              <StatusBadge
                label={approver.displayLabel || "ไม่อนุมัติ (อัตโนมัติ)"}
                bgColor={meta.color}
              />
            </Space>
          ) : (
            <StatusBadge label={approver.displayLabel || meta.label || "-"} bgColor={meta.color} />
          )}
          {(approver.status === "APPROVE" ||
            approver.status === "REJECT" ||
            approver.wasApprovedBeforeReject) && (
            <Text fontSize={14} color='background4'>
              {approver.dateApprove}
            </Text>
          )}
        </Space>
      </Col>
    </Row>
  );
};

export default ModalCheckStatusCredit;
